import { MoreVertical, Download, FileText, FileJson } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

interface ExportMenuProps {
  analysisData: any;
  telemetryData?: any;
  runId: string;
}

export function ExportMenu({ analysisData, telemetryData, runId }: ExportMenuProps) {
  const { toast } = useToast();

  const generateTimestamp = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16).replace(/[T:]/g, '-').replace(/-/g, '');
  };

  const exportJSON = () => {
    try {
      // FIX: Include taxonomy version if enriched data available
      const exportData = {
        run_id: runId,
        created_at: new Date().toISOString(),
        analysis: analysisData,
        telemetry: telemetryData,
        taxonomy_version: (analysisData as any)?.taxonomy_version || undefined,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `swing-analysis-${generateTimestamp()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: "JSON analysis report downloaded",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Could not export JSON file",
        variant: "destructive",
      });
    }
  };

  const exportPDF = async () => {
    try {
      // Dynamic import to avoid bundle size issues
      const { Document, Page, Text, View, StyleSheet, pdf } = await import('@react-pdf/renderer');
      
      const styles = StyleSheet.create({
        page: {
          flexDirection: 'column',
          backgroundColor: '#FFFFFF',
          padding: 30,
          fontFamily: 'Helvetica',
        },
        header: {
          marginBottom: 20,
          borderBottom: '1 solid #E2E8F0',
          paddingBottom: 10,
        },
        title: {
          fontSize: 24,
          fontWeight: 'bold',
          color: '#0F172A',
          marginBottom: 5,
        },
        subtitle: {
          fontSize: 12,
          color: '#64748B',
        },
        section: {
          marginBottom: 20,
        },
        sectionTitle: {
          fontSize: 16,
          fontWeight: 'bold',
          color: '#0F172A',
          marginBottom: 10,
        },
        text: {
          fontSize: 11,
          color: '#475569',
          lineHeight: 1.5,
          marginBottom: 5,
        },
        metric: {
          flexDirection: 'row',
          marginBottom: 5,
        },
        metricLabel: {
          fontSize: 11,
          color: '#64748B',
          width: 120,
        },
        metricValue: {
          fontSize: 11,
          color: '#0F172A',
          fontWeight: 'bold',
        },
        flaw: {
          marginBottom: 8,
          padding: 8,
          backgroundColor: '#FEF2F2',
          borderRadius: 4,
        },
        flawTitle: {
          fontSize: 12,
          fontWeight: 'bold',
          color: '#DC2626',
          marginBottom: 2,
        },
        drill: {
          marginBottom: 8,
          padding: 8,
          backgroundColor: '#F0FDF4',
          borderRadius: 4,
        },
        drillTitle: {
          fontSize: 12,
          fontWeight: 'bold',
          color: '#16A34A',
          marginBottom: 2,
        },
        footer: {
          position: 'absolute',
          bottom: 20,
          left: 20,
          right: 20,
        },
        footerText: {
          fontSize: 8,
          color: '#999999',
          textAlign: 'center',
        },
      });

      const MyDocument = () => (
        <Document>
          <Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <Text style={styles.title}>No Doubles — Swing Analysis Report</Text>
              <Text style={styles.subtitle}>
                Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
              </Text>
            </View>

            {analysisData?.key_metrics && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Key Metrics</Text>
                {Object.entries(analysisData.key_metrics).map(([key, value]: [string, any]) => (
                  <View key={key} style={styles.metric}>
                    <Text style={styles.metricLabel}>{key.replace(/_/g, ' ').toUpperCase()}:</Text>
                    <Text style={styles.metricValue}>{String(value)}</Text>
                  </View>
                ))}
              </View>
            )}

            {analysisData?.coach_narrative && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Coach Narrative</Text>
                <Text style={styles.text}>{analysisData.coach_narrative}</Text>
              </View>
            )}

            {Array.isArray(analysisData?.flaws) && analysisData.flaws.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Identified Flaws</Text>
                {analysisData.flaws.map((flaw: any, index: number) => (
                  <View key={index} style={styles.flaw}>
                    {/* FIX: Use enriched display_name if available */}
                    <Text style={styles.flawTitle}>{flaw.display_name || flaw.title || flaw.id || flaw.flaw_id}</Text>
                    {/* FIX: Use enriched description if available */}
                    <Text style={styles.text}>{flaw.short_description || flaw.description}</Text>
                    <Text style={styles.text}>
                      Severity: {flaw.severity} | Confidence: {flaw.confidence}{typeof flaw.confidence === 'string' ? '' : '%'}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {Array.isArray(analysisData?.drills) && analysisData.drills.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Recommended Drills</Text>
                {analysisData.drills.map((drill: any, index: number) => (
                  <View key={index} style={styles.drill}>
                    <Text style={styles.drillTitle}>{drill.title}</Text>
                    <Text style={styles.text}>{drill.one_liner}</Text>
                  </View>
                ))}
              </View>
            )}

            {telemetryData && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Analysis Telemetry</Text>
                <View style={styles.metric}>
                  <Text style={styles.metricLabel}>MODEL:</Text>
                  <Text style={styles.metricValue}>{telemetryData.model || 'Unknown'}</Text>
                </View>
                <View style={styles.metric}>
                  <Text style={styles.metricLabel}>LATENCY:</Text>
                  <Text style={styles.metricValue}>
                    {telemetryData.latency_ms ? `${(telemetryData.latency_ms / 1000).toFixed(1)}s` : 'Unknown'}
                  </Text>
                </View>
                <View style={styles.metric}>
                  <Text style={styles.metricLabel}>TOKENS IN:</Text>
                  <Text style={styles.metricValue}>
                    {telemetryData.tokens_in ? `${(telemetryData.tokens_in / 1000).toFixed(1)}k` : 'Unknown'}
                  </Text>
                </View>
                <View style={styles.metric}>
                  <Text style={styles.metricLabel}>TOKENS OUT:</Text>
                  <Text style={styles.metricValue}>
                    {telemetryData.tokens_out ? `${(telemetryData.tokens_out / 1000).toFixed(1)}k` : 'Unknown'}
                  </Text>
                </View>
                <View style={styles.metric}>
                  <Text style={styles.metricLabel}>EST. COST:</Text>
                  <Text style={styles.metricValue}>
                    {telemetryData.est_cost_usd ? `$${telemetryData.est_cost_usd.toFixed(3)}` : 'Unknown'}
                  </Text>
                </View>
              </View>
            )}
            
            {/* FIX: Add taxonomy version footer */}
            {(analysisData as any)?.taxonomy_version && (
              <View style={styles.footer}>
                <Text style={styles.footerText}>
                  Taxonomy {(analysisData as any).taxonomy_version}
                </Text>
              </View>
            )}
          </Page>
        </Document>
      );

      const blob = await pdf(<MyDocument />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `swing-analysis-${generateTimestamp()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: "PDF analysis report downloaded",
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: "Export Failed",
        description: "Could not export PDF file",
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportJSON}>
          <FileJson className="mr-2 h-4 w-4" />
          Export JSON
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportPDF}>
          <FileText className="mr-2 h-4 w-4" />
          Export PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}